/**
 * Metrica standard date display: dd-mmm-yy (e.g. "15-Jan-24")
 * Use this for all user-facing dates across Metrica apps.
 */

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

const MONTH_INDEX: Record<string, number> = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
};

const expandTwoDigitYear = (year: number): number => (year < 100 ? 2000 + year : year);

const isValidYmd = (year: number, month: number, day: number): boolean => {
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
};

/** Local YYYY-MM-DD for date inputs and API. */
export const toIsoDateString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Parse user-typed dates (dd-mmm-yy, dd/mm/yy, ISO) into a local Date.
 * Returns null when the value is empty or not a real calendar day.
 */
export const parseUserDateInput = (value: string | null | undefined): Date | null => {
  if (!value || !String(value).trim()) return null;
  const text = String(value).trim();

  const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const year = Number(iso[1]);
    const month = Number(iso[2]);
    const day = Number(iso[3]);
    if (!isValidYmd(year, month, day)) return null;
    return new Date(year, month - 1, day);
  }

  const named = text.match(/^(\d{1,2})[-.\s/]+([A-Za-z]{3})[-.\s/]+(\d{2}|\d{4})$/);
  if (named) {
    const day = Number(named[1]);
    const month = MONTH_INDEX[named[2].toLowerCase()];
    const year = expandTwoDigitYear(Number(named[3]));
    if (month == null || !isValidYmd(year, month + 1, day)) return null;
    return new Date(year, month, day);
  }

  const numeric = text.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2}|\d{4})$/);
  if (numeric) {
    const day = Number(numeric[1]);
    const month = Number(numeric[2]);
    const year = expandTwoDigitYear(Number(numeric[3]));
    if (!isValidYmd(year, month, day)) return null;
    return new Date(year, month - 1, day);
  }

  return parseToDate(text);
};

/** Normalize any stored date to YYYY-MM-DD, or '' if missing/invalid. */
export const toIsoDateOnly = (value?: string | null): string => {
  if (!value) return '';
  const parsed = parseUserDateInput(value) || parseToDate(String(value));
  return parsed ? toIsoDateString(parsed) : '';
};

function parseToDate(dateString: string): Date | null {
  if (!dateString || !String(dateString).trim()) return null;

  let date: Date;

  if (dateString.includes('T')) {
    date = new Date(dateString);
  } else if (/^\d{4}-\d{2}-\d{2}/.test(dateString)) {
    const [year, month, day] = dateString.split(/[-T]/).map((p) => parseInt(p, 10));
    date = new Date(year, month - 1, day);
  } else {
    date = new Date(dateString);
  }

  if (Number.isNaN(date.getTime())) return null;
  return date;
}

/** Format any date string as dd-mmm-yy. Returns empty string if invalid/missing. */
export const formatMetricaDate = (dateString: string | null | undefined): string => {
  try {
    if (!dateString) return '';
    const date = parseToDate(String(dateString));
    if (!date) return '';

    const dayStr = date.getDate().toString().padStart(2, '0');
    const monthStr = MONTH_NAMES[date.getMonth()];
    const yearStr = date.getFullYear().toString().slice(-2);

    return `${dayStr}-${monthStr}-${yearStr}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return '';
  }
};

/** @deprecated Prefer formatMetricaDate — kept as alias for existing imports. */
export const formatDateWithMonthName = formatMetricaDate;
